import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, ShieldX, Search, RefreshCw,
  Calendar, User, FileText, MapPin, Users, Copy, Check
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Detail({ icon: Icon, label, value, mono }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-white/[0.05] last:border-0">
      <div className="w-7 h-7 rounded bg-white/[0.04] flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-white/40" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-white/30 font-semibold mb-0.5">{label}</p>
        <p className={`text-sm text-white break-words ${mono ? "font-mono tracking-widest text-[#D4AF37]" : ""}`}>{value}</p>
      </div>
    </div>
  );
}

export default function CertVerifyPage() {
  const { certId } = useParams();
  const [query, setQuery] = useState(certId || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  const verify = async (id) => {
    const target = (id || query).trim().toUpperCase();
    if (!target) return;
    setLoading(true);
    setNotFound(false);
    setResult(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/notarize/verify/${target}`);
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (certId) verify(certId);
  }, [certId]);

  const handleCopy = () => {
    navigator.clipboard.writeText(result?.certificate_id || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const fmtDate = (iso) => {
    if (!iso) return null;
    return new Date(iso).toLocaleString("en-US", {
      month: "long", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZoneName: "short"
    });
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col">
      {/* Header */}
      <div className="border-b border-white/[0.06] px-6 py-4">
        <Link to="/" className="flex items-center gap-2.5 w-fit">
          <div className="w-8 h-8 bg-[#D4AF37] rounded flex items-center justify-center">
            <span className="text-black font-black text-xs">DF</span>
          </div>
          <span className="text-white font-bold tracking-tight">DealForge</span>
          <span className="text-white/20 text-sm">/ Certificate Verify</span>
        </Link>
      </div>

      {/* Hero */}
      <div className="flex-1 flex flex-col items-center justify-start px-4 pt-16 pb-12">
        <div className="w-full max-w-lg">
          {/* Title */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 flex items-center justify-center mx-auto mb-5">
              <ShieldCheck className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Certificate Verification</h1>
            <p className="text-white/40 text-sm max-w-sm mx-auto">
              Enter a DealForge notarization certificate ID to verify its authenticity and details.
            </p>
          </div>

          {/* Search */}
          <div className="flex gap-2 mb-8">
            <div className="flex-1 relative">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && verify()}
                placeholder="DF-XXXX-XXXX-XXXX"
                data-testid="cert-id-input"
                className="w-full bg-white/[0.04] border border-white/[0.1] rounded-xl px-4 py-3 text-white font-mono tracking-widest placeholder:text-white/20 placeholder:tracking-normal focus:outline-none focus:border-[#D4AF37]/50 text-sm"
              />
            </div>
            <button
              onClick={() => verify()}
              disabled={loading || !query.trim()}
              data-testid="verify-btn"
              className="px-5 py-3 bg-[#D4AF37] hover:bg-[#D4AF37]/90 disabled:opacity-50 text-black font-bold rounded-xl transition-all flex items-center gap-2"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {loading ? "Verifying…" : "Verify"}
            </button>
          </div>

          {/* Not found */}
          {notFound && (
            <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-2xl p-6 text-center" data-testid="cert-not-found">
              <ShieldX className="w-10 h-10 text-[#FF3B30] mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-1">Certificate Not Found</h3>
              <p className="text-white/40 text-sm">No notarization record matches this certificate ID. Please check the ID and try again.</p>
            </div>
          )}

          {/* Result */}
          {result && (
            <div
              className={`rounded-2xl border overflow-hidden ${
                result.valid
                  ? "border-[#00FF88]/20 bg-[#00FF88]/[0.02]"
                  : "border-[#FF3B30]/20 bg-[#FF3B30]/[0.02]"
              }`}
              data-testid="cert-result"
            >
              {/* Status banner */}
              <div className={`px-6 py-4 flex items-center gap-3 ${result.valid ? "bg-[#00FF88]/[0.06]" : "bg-[#FF3B30]/[0.06]"}`}>
                {result.valid
                  ? <ShieldCheck className="w-6 h-6 text-[#00FF88] flex-shrink-0" />
                  : <ShieldX className="w-6 h-6 text-[#FF3B30] flex-shrink-0" />
                }
                <div className="flex-1">
                  <p className={`font-bold text-sm uppercase tracking-wider ${result.valid ? "text-[#00FF88]" : "text-[#FF3B30]"}`}>
                    {result.valid ? "Valid — Authentically Notarized" : "Invalid — Not Notarized"}
                  </p>
                  <p className="text-white/40 text-xs mt-0.5">
                    {result.valid
                      ? "This document has been electronically notarized via DealForge."
                      : "This certificate does not represent a completed notarization."}
                  </p>
                </div>
                {result.valid && (
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.06] hover:bg-white/10 border border-white/[0.08] rounded-lg text-xs text-white/60 hover:text-white transition-all"
                  >
                    {copied ? <Check className="w-3 h-3 text-[#00FF88]" /> : <Copy className="w-3 h-3" />}
                    {copied ? "Copied" : "Copy ID"}
                  </button>
                )}
              </div>

              {/* Details */}
              <div className="px-6 pb-4 pt-2">
                <Detail icon={ShieldCheck} label="Certificate ID" value={result.certificate_id} mono />
                <Detail icon={FileText} label="Document" value={result.document_name} />
                <Detail icon={MapPin} label="Property" value={result.property_address} />
                <Detail icon={User} label="Notarized By" value={result.notary_name} />
                <Detail icon={User} label="Requested By" value={result.requestor_name} />
                <Detail icon={Calendar} label="Notarized On" value={fmtDate(result.notarized_at)} />
                {result.parties?.length > 0 && (
                  <div className="flex items-start gap-3 py-3">
                    <div className="w-7 h-7 rounded bg-white/[0.04] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Users className="w-3.5 h-3.5 text-white/40" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[10px] uppercase tracking-wider text-white/30 font-semibold mb-2">Parties</p>
                      <div className="flex flex-wrap gap-2">
                        {result.parties.map((p, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-white/[0.04] border border-white/[0.06] rounded text-white/70">
                            {p.name} <span className="text-white/30">· {p.role}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer stamp */}
              {result.valid && (
                <div className="px-6 pb-5">
                  <div className="flex items-center gap-2 px-3 py-2 bg-[#D4AF37]/[0.05] border border-[#D4AF37]/15 rounded-lg">
                    <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                    <p className="text-[10px] text-[#D4AF37]/70">
                      This certificate is cryptographically recorded on DealForge's notarization ledger and cannot be forged.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No action yet */}
          {!result && !notFound && !loading && (
            <div className="text-center text-white/20 text-sm">
              Enter a certificate ID above to check its validity
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/[0.04] px-6 py-4 text-center">
        <p className="text-white/20 text-xs">&copy; 2026 DealForge™ — Real Estate Transaction Infrastructure</p>
      </div>
    </div>
  );
}
